import React, { useEffect, useRef } from "react";

/**
 * Particle wave background: a WebGL2 point field (ported from the Akilira
 * landing hero to plain JS).
 *
 * Thousands of soft dots sit on an ordered grid that rides a moving wave
 * surface. Each dot is a damped spring pulled towards its place on that
 * surface. On mount the dots scatter in a shell around the centre, so the
 * first second is the cloud collapsing into the sheet. After that the sheet
 * keeps flowing and slowly turns. A pointer over the host pushes dots aside.
 *
 * Rendering pauses off screen and in a hidden tab. Reduced motion shows the
 * formed sheet, still. Every GL resource and listener is released on unmount.
 * Falls back to nothing (transparent canvas) where WebGL2 is unavailable.
 */

const TAU = Math.PI * 2;
const CAMZ = 4.25;
const FOV = 0.95;
const STIFF = 26;
const DAMP = 2 * Math.sqrt(STIFF) * 0.92;
const REACH = 0.6;

// Fewer, softer dots than the Akilira hero: this section is a short, wide band,
// so a dense sheet seen edge-on piles up and blows out to white.
const SETTINGS = { count: 7000, pitch: 0.4, rot: 0.11, fitw: 4.7, fith: 1.6, size: 1.5, sizevar: 0.17, zoom: 1 };

const VERTEX = `#version 300 es
in vec3 a_pos; in vec3 a_col; in float a_size;
uniform mat4 u_mvp; uniform mat4 u_mv; uniform float u_dpr; uniform float u_scale;
out vec3 v_col; out float v_fade;
void main(){
  float depth = -(u_mv * vec4(a_pos, 1.0)).z;
  gl_Position = u_mvp * vec4(a_pos, 1.0);
  gl_PointSize = clamp(a_size * u_dpr * u_scale * (4.6 / max(depth, 2.2)), u_dpr, 12.0 * u_dpr);
  v_col = a_col;
  v_fade = mix(0.62, 1.0, smoothstep(9.0, 2.0, depth));
}`;

const FRAGMENT = `#version 300 es
precision highp float;
in vec3 v_col; in float v_fade;
uniform float u_opacity;
out vec4 o;
void main(){
  vec2 q = gl_PointCoord - 0.5;
  float a = exp(-dot(q, q) * 5.0) - 0.06;
  if (a < 0.004) discard;
  a = clamp(a, 0.0, 1.0) * v_fade * u_opacity;
  o = vec4(v_col * a, a);
}`;

function parseHex(value) {
  let h = String(value || "").trim().replace("#", "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const n = parseInt(h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Smooth 2D value noise in [-0.5, 0.5]. */
function makeNoise() {
  const table = new Float32Array(512);
  for (let i = 0; i < 512; i++) table[i] = Math.random();
  const hash = (xi, yi) => table[((Math.imul(xi, 73856093) ^ Math.imul(yi, 19349663)) >>> 0) & 511];
  return (x, y) => {
    const xi = Math.floor(x), yi = Math.floor(y);
    const xf = x - xi, yf = y - yi;
    const u = xf * xf * (3 - 2 * xf);
    const v = yf * yf * (3 - 2 * yf);
    const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v - 0.5;
  };
}

function multiply(a, b, out) {
  for (let c = 0; c < 4; c++) {
    const b0 = b[c * 4], b1 = b[c * 4 + 1], b2 = b[c * 4 + 2], b3 = b[c * 4 + 3];
    out[c * 4] = a[0] * b0 + a[4] * b1 + a[8] * b2 + a[12] * b3;
    out[c * 4 + 1] = a[1] * b0 + a[5] * b1 + a[9] * b2 + a[13] * b3;
    out[c * 4 + 2] = a[2] * b0 + a[6] * b1 + a[10] * b2 + a[14] * b3;
    out[c * 4 + 3] = a[3] * b0 + a[7] * b1 + a[11] * b2 + a[15] * b3;
  }
}

function perspective(out, fovy, aspect, near, far) {
  const f = 1 / Math.tan(fovy / 2);
  const nf = 1 / (near - far);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) * nf;
  out[11] = -1;
  out[14] = 2 * far * near * nf;
}

function model(out, ry, rx, k) {
  const cy = Math.cos(ry), sy = Math.sin(ry), cx = Math.cos(rx), sx = Math.sin(rx);
  out[0] = cy * k; out[1] = 0; out[2] = -sy * k; out[3] = 0;
  out[4] = sy * sx * k; out[5] = cx * k; out[6] = cy * sx * k; out[7] = 0;
  out[8] = sy * cx * k; out[9] = -sx * k; out[10] = cy * cx * k; out[11] = 0;
  out[12] = 0; out[13] = 0; out[14] = 0; out[15] = 1;
}

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function startField(canvas, { color, bg, opacity }) {
  let gl = null;
  try {
    gl = canvas.getContext("webgl2", { antialias: false, alpha: true, premultipliedAlpha: true, powerPreference: "low-power" });
  } catch (e) { gl = null; }
  if (!gl) return () => {};

  const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  const program = gl.createProgram();
  if (!vs || !fs || !program) return () => {};
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return () => {};

  const settings = SETTINGS;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const small = window.innerWidth < 820;
  const N = Math.round(settings.count * (small ? 0.7 : 1));
  const noise = makeNoise();

  const pos = new Float32Array(N * 3);
  const vel = new Float32Array(N * 3);
  const tgt = new Float32Array(N * 3);
  const col = new Float32Array(N * 3);
  const mixv = new Float32Array(N);
  const size = new Float32Array(N);

  for (let i = 0; i < N; i++) {
    const u = Math.random() * 2 - 1;
    const th = Math.random() * TAU;
    const r = 2 + Math.random() * 2.4;
    const s = Math.sqrt(1 - u * u);
    pos[i * 3] = s * Math.cos(th) * r;
    pos[i * 3 + 1] = u * r;
    pos[i * 3 + 2] = s * Math.sin(th) * r;
    size[i] = 1 + (Math.random() * 2 - 1) * settings.sizevar;
  }

  const cols = Math.max(8, Math.round(Math.sqrt(N * 2.4)));
  const rows = Math.max(2, Math.floor(N / cols));
  const surface = (t) => {
    for (let i = 0; i < N; i++) {
      const i3 = i * 3;
      const x = ((i % cols) / (cols - 1)) * 2 - 1;
      const z = (Math.floor(i / cols) / Math.max(1, rows - 1)) * 2 - 1;
      const X = x * 2.35;
      const Z = z * 1.05;
      const swell = Math.sin(X * 1.6 - t * 0.85);
      tgt[i3] = X;
      tgt[i3 + 1] = swell * 0.15 + Math.sin(Z * 2.1 + t * 0.62) * 0.1 + noise(X * 0.9 + t * 0.18, Z * 1.2) * 0.19;
      tgt[i3 + 2] = Z;
      mixv[i] = 0.25 + 0.5 * (0.5 + 0.5 * swell);
    }
  };

  // Palette: the brand purple glowing on a dark ground; troughs lean toward
  // the ground colour. Additive blending, so overlapping dots brighten.
  const base = parseHex(color) || [0.79, 0.66, 1];
  const ground = parseHex(bg) || [0.11, 0.07, 0.21];
  const toward = 0.3;
  const acc = [
    base[0] + (ground[0] - base[0]) * toward,
    base[1] + (ground[1] - base[1]) * toward,
    base[2] + (ground[2] - base[2]) * toward,
  ];

  surface(0);
  if (reduced) pos.set(tgt);

  const vao = gl.createVertexArray();
  gl.bindVertexArray(vao);
  const attrib = (name, data, width, usage) => {
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, usage);
    const loc = gl.getAttribLocation(program, name);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, width, gl.FLOAT, false, 0, 0);
    return buffer;
  };
  const bPos = attrib("a_pos", pos, 3, gl.DYNAMIC_DRAW);
  const bCol = attrib("a_col", col, 3, gl.DYNAMIC_DRAW);
  const bSize = attrib("a_size", size, 1, gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  const uMvp = gl.getUniformLocation(program, "u_mvp");
  const uMv = gl.getUniformLocation(program, "u_mv");
  const uDpr = gl.getUniformLocation(program, "u_dpr");
  const uScale = gl.getUniformLocation(program, "u_scale");
  const uOpacity = gl.getUniformLocation(program, "u_opacity");

  const P = new Float32Array(16), V = new Float32Array(16), M = new Float32Array(16);
  const MV = new Float32Array(16), MVP = new Float32Array(16);
  V[0] = V[5] = V[10] = V[15] = 1;
  V[14] = -CAMZ;

  let yaw = 0, scale = 1, dpr = 1, W = 0, H = 0, sizeDirty = true;
  const pointer = { x: 0, y: 0, sx: 0, sy: 0, on: false };

  const resize = () => {
    const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
    if (!sizeDirty && dpr === nextDpr) return;
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    sizeDirty = false;
    dpr = nextDpr;
    W = Math.max(1, Math.round(r.width * dpr));
    H = Math.max(1, Math.round(r.height * dpr));
    canvas.width = W;
    canvas.height = H;
    const half = Math.tan(FOV / 2) * CAMZ;
    const byW = (2 * half * (r.width / r.height) * 0.98) / settings.fitw;
    const byH = (2 * half * 0.86) / settings.fith;
    scale = Math.max(0.35, Math.min(byW, byH)) * settings.zoom;
    gl.viewport(0, 0, W, H);
  };

  const draw = () => {
    perspective(P, FOV, W / Math.max(1, H), 0.1, 60);
    model(M, yaw + pointer.x * 0.18, settings.pitch + pointer.y * 0.09, scale);
    multiply(V, M, MV);
    multiply(P, MV, MVP);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.useProgram(program);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, bPos);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, pos);
    gl.bindBuffer(gl.ARRAY_BUFFER, bCol);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, col);
    gl.uniformMatrix4fv(uMvp, false, MVP);
    gl.uniformMatrix4fv(uMv, false, MV);
    gl.uniform1f(uDpr, dpr);
    gl.uniform1f(uScale, settings.size * Math.max(scale, 0.9));
    gl.uniform1f(uOpacity, opacity);
    gl.drawArrays(gl.POINTS, 0, N);
    gl.bindVertexArray(null);
  };

  const step = (now, dt) => {
    const t = now / 1000;
    yaw += dt * settings.rot;
    const ease = Math.min(1, dt * 5);
    pointer.x += ((pointer.on ? pointer.sx : 0) - pointer.x) * ease;
    pointer.y += ((pointer.on ? pointer.sy : 0) - pointer.y) * ease;
    surface(t);

    const ry = yaw + pointer.x * 0.18;
    const rx = settings.pitch + pointer.y * 0.09;
    const tanF = Math.tan(FOV / 2);
    let dx = pointer.sx * tanF * (W / Math.max(1, H));
    let dy = pointer.sy * tanF;
    let dz = -1;
    const dl = Math.hypot(dx, dy, dz);
    dx /= dl; dy /= dl; dz /= dl;
    const cyr = Math.cos(-ry), syr = Math.sin(-ry), cxr = Math.cos(-rx), sxr = Math.sin(-rx);
    const toLocal = (vx, vy, vz) => {
      const x1 = vx * cyr + vz * syr;
      const z1 = -vx * syr + vz * cyr;
      return [x1 / scale, (vy * cxr - z1 * sxr) / scale, (vy * sxr + z1 * cxr) / scale];
    };
    const [ox, oy, oz] = toLocal(0, 0, CAMZ);
    let [lx, ly, lz] = toLocal(dx, dy, dz);
    const ln = Math.hypot(lx, ly, lz) || 1;
    lx /= ln; ly /= ln; lz /= ln;

    const reach2 = REACH * REACH;
    const push = pointer.on;
    const tint = Math.min(1, dt * 3.2);
    const [bR, bG, bB] = base;
    const [aR, aG, aB] = acc;

    for (let i = 0; i < N; i++) {
      const i3 = i * 3;
      const px = pos[i3], py = pos[i3 + 1], pz = pos[i3 + 2];
      let ax = (tgt[i3] - px) * STIFF - vel[i3] * DAMP;
      let ay = (tgt[i3 + 1] - py) * STIFF - vel[i3 + 1] * DAMP;
      let az = (tgt[i3 + 2] - pz) * STIFF - vel[i3 + 2] * DAMP;
      if (push) {
        const wx = px - ox, wy = py - oy, wz = pz - oz;
        const along = wx * lx + wy * ly + wz * lz;
        if (along > 0) {
          const ex = wx - lx * along, ey = wy - ly * along, ez = wz - lz * along;
          const d2 = ex * ex + ey * ey + ez * ez;
          if (d2 < reach2) {
            const d = Math.sqrt(d2) + 1e-4;
            let k = 1 - d2 / reach2;
            k = k * k * 48;
            ax += (ex / d) * k; ay += (ey / d) * k; az += (ez / d) * k;
          }
        }
      }
      vel[i3] += ax * dt; vel[i3 + 1] += ay * dt; vel[i3 + 2] += az * dt;
      pos[i3] = px + vel[i3] * dt;
      pos[i3 + 1] = py + vel[i3 + 1] * dt;
      pos[i3 + 2] = pz + vel[i3 + 2] * dt;
      const m = mixv[i];
      col[i3] += (bR + (aR - bR) * m - col[i3]) * tint;
      col[i3 + 1] += (bG + (aG - bG) * m - col[i3 + 1]) * tint;
      col[i3 + 2] += (bB + (aB - bB) * m - col[i3 + 2]) * tint;
    }
  };

  const still = () => {
    for (let i = 0; i < N; i++) {
      const m = mixv[i];
      col[i * 3] = base[0] + (acc[0] - base[0]) * m;
      col[i * 3 + 1] = base[1] + (acc[1] - base[1]) * m;
      col[i * 3 + 2] = base[2] + (acc[2] - base[2]) * m;
    }
    resize();
    if (W > 1 && H > 1) draw();
  };

  let raf = 0, alive = false, onScreen = true, last = 0;
  const frame = (now) => {
    if (!alive) return;
    raf = requestAnimationFrame(frame);
    const dt = Math.max(0, Math.min((now - last) / 1000, 0.05));
    last = now;
    resize();
    if (W < 2 || H < 2) return;
    step(now, dt);
    draw();
  };
  const start = () => {
    if (reduced || alive || !onScreen || document.hidden) return;
    alive = true;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  };
  const stop = () => {
    alive = false;
    cancelAnimationFrame(raf);
  };

  const io = new IntersectionObserver((entries) => {
    onScreen = entries.some((e) => e.isIntersecting);
    if (onScreen) start(); else stop();
  }, { rootMargin: "260px 0px" });
  io.observe(canvas);
  const ro = new ResizeObserver(() => {
    sizeDirty = true;
    if (reduced) still();
  });
  ro.observe(canvas);
  const onVisibility = () => { if (document.hidden) stop(); else start(); };
  document.addEventListener("visibilitychange", onVisibility);

  // The canvas takes no events, so listen on what sits over it.
  const host = canvas.parentElement || document.body;
  const onMove = (e) => {
    const r = canvas.getBoundingClientRect();
    pointer.sx = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointer.sy = -(((e.clientY - r.top) / r.height) * 2 - 1);
    pointer.on = true;
  };
  const onLeave = () => { pointer.on = false; };
  if (!reduced) {
    host.addEventListener("pointermove", onMove, { passive: true });
    host.addEventListener("pointerleave", onLeave, { passive: true });
    host.addEventListener("pointercancel", onLeave, { passive: true });
  }

  if (reduced) still(); else start();

  return () => {
    stop();
    io.disconnect();
    ro.disconnect();
    document.removeEventListener("visibilitychange", onVisibility);
    host.removeEventListener("pointermove", onMove);
    host.removeEventListener("pointerleave", onLeave);
    host.removeEventListener("pointercancel", onLeave);
    gl.deleteBuffer(bPos);
    gl.deleteBuffer(bCol);
    gl.deleteBuffer(bSize);
    gl.deleteVertexArray(vao);
    gl.deleteProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
  };
}

// Vivid brand purple on a near-black ground, the way Akilira's pink sits on black.
const DotWave = ({ className, color = "#b388ff", bg = "#0a0812", opacity = 0.85 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    return startField(canvas, { color, bg, opacity });
  }, [color, bg, opacity]);
  return <canvas ref={ref} className={className} aria-hidden="true" />;
};

export default DotWave;
